import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { OfficeItemDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { ask, askText } from '../dialogs';
import { Icon } from '../icons';
import { Empty, ErrorBanner, useNow } from '../ui';
import { AgentAvatar, timeOf } from './OfficePage';

/**
 * Da approvare: quello che la squadra ha preparato e aspetta il sì di Marco.
 * - Risposte e solleciti: si leggono, si correggono e partono con «Approva e invia».
 * - Da richiamare: chi ha chiesto una telefonata, con il numero.
 * - Avvisi: siti fermi, mail sospette, scadenze dei servizi.
 * - Bandi e voucher trovati, con il link ufficiale e il primo passo.
 */

type Tab = 'risposte' | 'richiamare' | 'avvisi' | 'bandi' | 'fatte';

const TABS: { key: Tab; label: string; kinds: OfficeItemDto['kind'][] }[] = [
  { key: 'risposte', label: 'Risposte', kinds: ['risposta', 'sollecito'] },
  { key: 'richiamare', label: 'Da richiamare', kinds: ['richiamare'] },
  { key: 'avvisi', label: 'Avvisi', kinds: ['avviso'] },
  { key: 'bandi', label: 'Bandi e voucher', kinds: ['bando'] },
  { key: 'fatte', label: 'Già fatte', kinds: [] },
];

const splitList = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

function OriginalMail({ ctx, item }: { ctx: Ctx; item: OfficeItemDto }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!item.mail) return null;
  const toggle = async () => {
    setOpen((o) => !o);
    if (text !== null || !item.mail) return;
    try {
      const m = await ctx.api.get<{ text: string }>(`/api/mail/messages/${item.mail.uid}?folder=${encodeURIComponent(item.mail.folder)}`);
      setText(m.text || '(mail senza testo)');
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <div className="ap-orig">
      <button type="button" className="of-link" onClick={toggle}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} /> {item.kind === 'sollecito' ? 'Leggi la tua mail di allora' : 'Leggi la mail arrivata'}
      </button>
      {open && <div className="ap-orig-text">{error ? <span className="bad">{error}</span> : text === null ? 'Caricamento…' : text.slice(0, 8000)}</div>}
    </div>
  );
}

function ReplyCard({ ctx, item, now, onDone }: { ctx: Ctx; item: OfficeItemDto; now: number; onDone: () => void }) {
  const d = item.draft!;
  const [to, setTo] = useState(d.to.join(', '));
  const [subject, setSubject] = useState(d.subject);
  const [text, setText] = useState(d.text);
  const [signature, setSignature] = useState(d.signature);
  const [showSign, setShowSign] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const changed = to !== d.to.join(', ') || subject !== d.subject || text !== d.text || signature !== d.signature;
  const missing = /\[DA COMPLETARE/i.test(text);
  const body = () => ({ to: splitList(to), subject, text, signature });

  const approve = async () => {
    if (missing && !(await ask('Nel testo c’è ancora qualcosa «DA COMPLETARE». Vuoi inviarla lo stesso?', { title: 'Manca qualcosa', ok: 'Invia lo stesso' }))) return;
    setBusy(true);
    setError(null);
    try {
      await ctx.api.post(`/api/office/items/${item.id}/approve`, body());
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      await ctx.api.put(`/api/office/items/${item.id}`, body());
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const discard = async () => {
    const why = await askText('Perché la scarti?', '', 'Facoltativo: diventa una regola per le prossime volte.');
    if (why === null) return;
    setBusy(true);
    try {
      await ctx.api.post(`/api/office/items/${item.id}/discard`, { why: why.trim() || undefined });
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <article className={`ap-card${item.careful ? ' careful' : ''}`}>
      <header className="ap-card-head">
        <AgentAvatar code={item.agent} size={34} />
        <div className="grow">
          <b>{item.kind === 'sollecito' ? 'Sollecito' : 'Risposta'} a {item.mail?.fromName || d.to[0]}</b>
          <div className="muted small">
            Preparata {timeOf(item.createdAt, now)}
            {item.mail && (
              <>
                {' '}
                · {item.kind === 'sollecito' ? 'la tua mail' : 'mail'} «{item.mail.subject || 'senza oggetto'}»
              </>
            )}
          </div>
        </div>
      </header>
      {item.summary && <p className="ap-summary">{item.summary}</p>}
      {item.careful && (
        <div className="ap-careful">
          <Icon name="bell" size={16} />
          <span>
            <b>Controlla bene:</b> {item.carefulWhy ?? 'tocca prezzi, date o impegni.'}
          </span>
        </div>
      )}
      <OriginalMail ctx={ctx} item={item} />
      <div className="ap-form">
        <label>
          <span>A</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          <span>Oggetto</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </label>
        <textarea className={missing ? 'missing' : ''} value={text} rows={Math.min(16, Math.max(5, text.split('\n').length + 1))} onChange={(e) => setText(e.target.value)} />
        {missing && <div className="ap-missing small">Completa le parti tra parentesi quadre «DA COMPLETARE» prima di inviare.</div>}
        <button type="button" className="of-link" onClick={() => setShowSign((s) => !s)}>
          <Icon name={showSign ? 'chevronDown' : 'chevronRight'} size={14} /> Firma
        </button>
        {showSign ? <textarea className="ap-sign" rows={Math.max(2, signature.split('\n').length)} value={signature} onChange={(e) => setSignature(e.target.value)} /> : <pre className="ap-sign-view">{signature}</pre>}
      </div>
      {item.reason && (
        <div className="ap-why">
          <span>Perché così:</span> {item.reason}
        </div>
      )}
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <footer className="ap-actions">
        <button type="button" className="btn ghost" disabled={busy} onClick={discard}>
          Scarta
        </button>
        <span className="grow" />
        {changed && (
          <button type="button" className="btn ghost" disabled={busy} onClick={save}>
            {saved ? 'Salvata' : 'Salva le modifiche'}
          </button>
        )}
        <button type="button" className="btn good" disabled={busy || !splitList(to).length || !text.trim()} onClick={approve}>
          <Icon name="send" size={15} /> {busy ? 'Invio…' : 'Approva e invia'}
        </button>
      </footer>
    </article>
  );
}

function SimpleCard({ ctx, item, now, onDone }: { ctx: Ctx; item: OfficeItemDto; now: number; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = async (path: 'done' | 'discard', bodyIn?: unknown) => {
    setBusy(true);
    try {
      await ctx.api.post(`/api/office/items/${item.id}/${path}`, bodyIn ?? {});
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const level = item.alert?.level ?? 'warn';
  const daysLeft = item.dueAt ? Math.ceil((new Date(item.dueAt).getTime() - now) / 86_400_000) : null;
  return (
    <article className={`ap-card simple k-${item.kind}${item.kind === 'avviso' ? ` lv-${level}` : ''}`}>
      <header className="ap-card-head">
        <AgentAvatar code={item.agent} size={34} />
        <div className="grow">
          <b>{item.kind === 'richiamare' ? item.callback?.who : item.title}</b>
          <div className="muted small ap-meta">
            {item.kind === 'bando' ? 'Trovato' : item.kind === 'richiamare' ? 'Chiesto' : 'Segnalato'} {timeOf(item.createdAt, now)}
            {item.kind === 'bando' && daysLeft !== null && <span className={`ap-due${daysLeft <= 14 ? ' soon' : ''}`}>{daysLeft <= 0 ? 'scade oggi' : `scade tra ${daysLeft} giorni`}</span>}
          </div>
        </div>
      </header>
      {item.kind === 'richiamare' && item.callback && (
        <div className="ap-call">
          {item.callback.phone ? (
            <a className="btn dark" href={`tel:${item.callback.phone.replace(/[^\d+]/g, '')}`}>
              <Icon name="phone" size={15} /> {item.callback.phone}
            </a>
          ) : (
            <span className="muted small">Numero non scritto nella mail: cercalo in rubrica.</span>
          )}
          <span>{item.callback.why}</span>
        </div>
      )}
      {item.kind !== 'richiamare' && item.summary && <p className="ap-summary pre">{item.summary}</p>}
      {item.kind === 'bando' && item.bando && (
        <dl className="ap-kv">
          {item.bando.ente && (
            <>
              <dt>Chi lo fa</dt>
              <dd>{item.bando.ente}</dd>
            </>
          )}
          {item.bando.perChi && (
            <>
              <dt>Per chi</dt>
              <dd>{item.bando.perChi}</dd>
            </>
          )}
          {item.bando.importo && (
            <>
              <dt>Quanto</dt>
              <dd>{item.bando.importo}</dd>
            </>
          )}
          {item.bando.scadenza && (
            <>
              <dt>Scadenza</dt>
              <dd>{new Date(item.bando.scadenza + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</dd>
            </>
          )}
          {item.bando.cosaFare && (
            <>
              <dt>Primo passo</dt>
              <dd>{item.bando.cosaFare}</dd>
            </>
          )}
        </dl>
      )}
      {item.reason && (
        <div className="ap-why">
          <span>{item.kind === 'bando' ? 'Da dove arriva:' : 'Perché:'}</span> {item.reason}
        </div>
      )}
      {item.kind === 'avviso' && item.mail && <OriginalMail ctx={ctx} item={item} />}
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <footer className="ap-actions">
        {item.kind === 'bando' && item.bando?.link && (
          <a className="btn ghost" href={item.bando.link} target="_blank" rel="noopener noreferrer">
            <Icon name="link" size={15} /> Pagina ufficiale
          </a>
        )}
        <span className="ap-btns">
        {item.kind === 'bando' ? (
          <>
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              title="Lo togli dalla lista"
              onClick={async () => {
                const w = await askText('Perché non interessa?', '', 'Facoltativo: aiuta Opportunità a cercare meglio.');
                if (w !== null) await act('discard', { why: w.trim() || undefined });
              }}
            >
              Non interessa
            </button>
            <button type="button" className="btn good" disabled={busy} title="Lo segni come da seguire: passa in «Già fatte»" onClick={() => act('done', { note: 'Interessa' })}>
              <Icon name="check" size={15} /> Mi interessa
            </button>
          </>
        ) : (
          <button type="button" className="btn good" disabled={busy} title="Passa in «Già fatte»" onClick={() => act('done', { note: item.kind === 'richiamare' ? 'Richiamato' : 'Visto' })}>
            <Icon name="check" size={15} /> {item.kind === 'richiamare' ? 'Fatto, l’ho richiamato' : 'Visto'}
          </button>
        )}
        </span>
      </footer>
    </article>
  );
}

function DoneRow({ item, now }: { item: OfficeItemDto; now: number }) {
  return (
    <div className={`ap-done s-${item.status}`}>
      <AgentAvatar code={item.agent} size={28} />
      <div className="grow">
        <b>{item.title}</b>
        <div className="muted small">
          {item.status === 'scartato' ? 'Scartato' : item.status === 'errore' ? 'Non riuscito' : 'Fatto'}
          {item.result && item.result !== 'Fatto' && item.result !== 'Scartato' ? `: ${item.result}` : ''}
          {item.doneAt ? ` · ${timeOf(item.doneAt, now)}` : ''}
        </div>
      </div>
    </div>
  );
}

export function ApprovalsPage({ ctx }: { ctx: Ctx }) {
  const param = (ctx.pageParam ?? {}) as { tab?: Tab; focus?: number };
  const [tab, setTab] = useState<Tab>(param.tab ?? 'risposte');
  const [open, setOpen] = useState<OfficeItemDto[] | null>(null);
  const [done, setDone] = useState<OfficeItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const now = useNow(30_000);

  const load = useCallback(async () => {
    try {
      const [o, d] = await Promise.all([ctx.api.get<OfficeItemDto[]>('/api/office/items?status=aperto'), ctx.api.get<OfficeItemDto[]>('/api/office/items?status=chiuso')]);
      setOpen(o);
      setDone(d);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [ctx.api]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (ctx.event?.type === 'office.items') void load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Se arrivo da una riga del diario, apro la scheda giusta
  useEffect(() => {
    if (!param.focus || !open) return;
    const it = open.find((x) => x.id === param.focus);
    const t = it ? TABS.find((x) => x.kinds.includes(it.kind)) : null;
    if (t) setTab(t.key);
    else setTab('fatte');
  }, [open?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const byTab = useMemo(() => {
    const m: Record<Tab, number> = { risposte: 0, richiamare: 0, avvisi: 0, bandi: 0, fatte: 0 };
    for (const it of open ?? []) for (const t of TABS) if (t.kinds.includes(it.kind)) m[t.key]++;
    return m;
  }, [open]);

  const list = tab === 'fatte' ? [] : (open ?? []).filter((x) => TABS.find((t) => t.key === tab)!.kinds.includes(x.kind));
  return (
    <div className="ap-page">
      <div className="of-head">
        <div>
          <h1>Da approvare</h1>
          <div className="muted">Quello che la squadra ha preparato: niente parte senza il tuo sì.</div>
        </div>
      </div>
      <div className="ap-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>
            {t.label}
            {t.key !== 'fatte' && byTab[t.key] > 0 && <span className="ap-count">{byTab[t.key]}</span>}
          </button>
        ))}
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!open ? (
        <div className="panel muted">Caricamento…</div>
      ) : tab === 'fatte' ? (
        <section className="panel ap-done-list">
          {(done ?? []).length === 0 ? <Empty icon="check">Ancora niente.</Empty> : (done ?? []).map((it) => <DoneRow key={it.id} item={it} now={now} />)}
        </section>
      ) : list.length === 0 ? (
        <div className="panel">
          <Empty icon="check">
            {tab === 'risposte' ? 'Nessuna risposta da approvare.' : tab === 'richiamare' ? 'Nessuno da richiamare.' : tab === 'avvisi' ? 'Nessun avviso: tutto a posto.' : 'Nessun bando nuovo.'}
          </Empty>
        </div>
      ) : (
        <div className={`ap-list${tab === 'risposte' ? ' wide' : ''}`}>
          {list.map((it) =>
            it.kind === 'risposta' || it.kind === 'sollecito' ? <ReplyCard key={it.id} ctx={ctx} item={it} now={now} onDone={load} /> : <SimpleCard key={it.id} ctx={ctx} item={it} now={now} onDone={load} />,
          )}
        </div>
      )}
    </div>
  );
}
